/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */

import forEach from 'lodash/forEach';
import kebabCase from 'lodash/kebabCase';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import {
	UFOExperience,
	ExperienceTypes,
	ExperiencePerformanceTypes,
} from '@atlassian/ufo/ufo-experimental';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { getAnalyticsWebClientPromise } from '@jira/platform__observability-product-analytics-web-client-async';
import type { RestPropertiesResponse, RestPropertiesOptions } from './types.tsx';

let ufoExperiencesMap: {
	[value: string]: UFOExperience;
} = {};
const getUfoExperience = (key: string) => {
	if (!ufoExperiencesMap[key]) {
		ufoExperiencesMap[key] = new UFOExperience(key, {
			type: ExperienceTypes.Operation,
			performanceType: ExperiencePerformanceTypes.Custom,
		});
	}
	return ufoExperiencesMap[key];
};
export const resetUfoExperiencesMap = (): void => {
	ufoExperiencesMap = {};
};
/**
 * Generates a URL for fetching frontend configuration settings, incorporating optional query parameters
 * for user, project, and instance-specific configurations. This dynamic construction allows for
 * flexible retrieval of settings based on the provided context, facilitating customized frontend
 * experiences.
 */
export const constructEndpoint = ({
	userProperties,
	projectId,
	projectProperties,
	instanceProperties,
}: RestPropertiesOptions): string => {
	const params = new URLSearchParams();
	userProperties && params.append('user', userProperties.join(','));
	projectId !== undefined && params.append('projectId', projectId);
	projectProperties && params.append('project', projectProperties.join(','));
	instanceProperties && params.append('instance', instanceProperties.join(','));
	return `/rest/internal/latest/frontendconfig/properties?${params.toString()}`;
};
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const patchProperties = (response: any, propertiesPath: string) =>
	response &&
	forEach(response[propertiesPath], (value, prop) => {
		try {
			response[propertiesPath][prop] = JSON.parse(value);
		} catch (error) {
			// TODO: Endpoint should ideally only return stringified values
			if (prop === 'initial.project.template.name') {
				response[propertiesPath][prop] = value;
			} else {
				throw error;
			}
		}
	});
/**
 * Initiates an asynchronous operation to obtain configuration properties, utilizing a constructed endpoint.
 * This function is instrumental in managing the request lifecycle, incorporating analytics to monitor
 * success or failure, and employs error handling strategies to ensure robust operation. It serves as a
 * critical component in fetching and applying configurations necessary for frontend customization.
 */
export const get = async (options: RestPropertiesOptions): Promise<RestPropertiesResponse> => {
	const ufoExperience = getUfoExperience(
		`${kebabCase(options.consumerMeta.packageName)}.${kebabCase(options.consumerMeta.id)}`,
	);
	try {
		ufoExperience?.start();
		const url = constructEndpoint(options);
		const response = await performGetRequest(url);
		if (response === null) {
			getAnalyticsWebClientPromise().then((client) => {
				const payload = {
					action: 'succeeded',
					actionSubject: 'jiraCommonRestInternalLatest.getConfigProperties',
					source: 'frontendConfigProperties',
					attributes: {
						package: consumerMeta.packageName,
						id: consumerMeta.id,
						isNullResponse: true,
					},
				};
				client.getInstance().sendOperationalEvent(payload);
			});
		}
		patchProperties(response, 'user');
		patchProperties(response, 'instance');
		patchProperties(response, 'project');
		const { consumerMeta } = options;
		getAnalyticsWebClientPromise().then((client) => {
			const payload = {
				source: 'frontendConfigProperties',
				action: 'success',
				actionSubject: `${consumerMeta.packageName}.${consumerMeta.id}`,
			};
			client.getInstance().sendOperationalEvent(payload);
		});
		ufoExperience?.success();

		return response || { user: {}, instance: {}, project: {} };
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		const { consumerMeta } = options;
		!isClientFetchError(error) &&
			fireErrorAnalytics({
				error,
				meta: {
					id: consumerMeta.id,
					packageName: consumerMeta.packageName,
					teamName: consumerMeta.teamName,
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		ufoExperience?.failure();
		throw error;
	}
};
